// One /volunteer socket per page, shared by everything that needs it.
//
// This used to live inside the chat panel's setup, which meant the socket only existed when
// the chat panel rendered — and /volunteer/me had no socket at all. Once on-deck alerts and
// the backstage timeline needed the same connection, opening a second one per feature would
// have meant two subscribes, two reconnect storms and two sources of truth about the roster.
//
// Everything here is additive: `subscribe()` may be called for several showings on one page
// (as /volunteer/me does), and handlers registered with `on()` survive reconnects.

'use strict';

// Rebuilt on every subscribe rather than trusted from the payload, matching the server's own
// "re-read fresh" rule — a page that missed an event still converges on reconnect.
const connections = {};

const tokenOf = () => (window.user && window.user.token) || '';

/**
 * Connect to /volunteer, or hand back the existing connection.
 * The namespace is shared, so one socket serves every showing this page cares about.
 */
function connect() {
	if (connections.socket) return connections.socket;

	connections.socket = window.io.connect('/volunteer', {query: 'token=' + tokenOf()});
	return connections.socket;
}

/**
 * A connection-state pill. Every surface in this feature shows one, because a stale timeline
 * that looks live is worse than one that admits it is disconnected — especially on a screen
 * nobody is watching.
 */
function bindConnectionPill(el, className) {
	if (!el) return () => {};

	const base = className || 'vol-chat-conn';
	const set = (state, label) => {
		el.textContent = label;
		el.className = base + ' ' + base + '-' + state;
	};

	const socket = connect();
	socket.on('connect', () => set('live', 'live'));
	socket.on('disconnect', () => set('down', 'reconnecting…'));
	socket.on('reconnect', () => set('live', 'live'));

	set(socket.connected ? 'live' : 'down', socket.connected ? 'live' : 'connecting…');
	return set;
}

/**
 * Subscribe to a showing and stay subscribed across reconnects.
 *
 * `onReady(data)` receives the server's full ack — roster, chat history, scene state, my
 * characters, and any unacknowledged notifications. It fires again after every reconnect
 * with fresh state, so callers should treat it as "here is the truth now", not "here is the
 * initial load", and render idempotently.
 */
function subscribe(showingId, {onReady, onError} = {}) {
	const socket = connect();

	const send = () => {
		socket.emit('subscribe', {showingId}, (err, data) => {
			if (err) {
				if (onError) onError(err);
				return;
			}
			if (onReady) onReady(data);
		});
	};

	if (socket.connected) send();
	socket.on('connect', send);

	return socket;
}

/**
 * Subscribe as the unattended wall display, which authenticates with a token rather than a
 * user. Gets scene changes and progress and nothing else.
 */
function subscribeDisplay(token, {onReady, onError} = {}) {
	const socket = connect();

	const send = () => {
		socket.emit('subscribe:display', {token}, (err, data) => {
			if (err) {
				if (onError) onError(err);
				return;
			}
			if (onReady) onReady(data);
		});
	};

	if (socket.connected) send();
	socket.on('connect', send);

	return socket;
}

module.exports = {connect, subscribe, subscribeDisplay, bindConnectionPill};
